import { useState } from 'react';
import { Button } from '@/ui/Button';
import { Dialog } from '@/ui/Dialog';
import { Skeleton } from '@/ui/Skeleton';
import { Tabs, TabPanel, type Pestana } from '@/ui/Tabs';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { cn } from '@/lib/cn';
import { Bandeja, PorAprobar } from './Aprobaciones';
import { FormularioSolicitud } from './FormularioSolicitud';
import { MisSolicitudes } from './MisSolicitudes';
import { Tipos } from './Tipos';
import {
  useCatalogo, usePorAprobar,
  type SolicitudDetalle, type TipoDeSolicitud,
} from './api';

/**
 * Solicitudes — trámites ante Bienestar.
 *
 * `module: null`: **la tiene cualquier rol**. Es la pantalla de mayor alcance
 * del portal después de Inicio, y por eso todo lo que falla aquí lo ve la
 * organización entera.
 *
 * ## Las cinco pestañas y quién ve cada una
 *
 *   Catálogo · Mis solicitudes    todos
 *   Por aprobar                   gestión SIEMPRE, y cualquier colaborador que
 *                                 tenga algo que firmar — es lo que permite que
 *                                 un jefe sin rol de administración apruebe
 *   Bandeja · Tipos               solo gestión
 *
 * ## La sub-ruta
 *
 * `#tramites/<vista>` entra directo a una pestaña, y el mapa de abajo traduce
 * los nombres en español. Es por donde entran los enlaces de Inicio
 * (`#tramites/mis`, `#tramites/aprobaciones`).
 *
 * **Nada de inventar hashes.** Un alias que no existe en el `HASH_ALIAS` no da
 * error: `navigate()` no encuentra la clave y manda a Inicio. Y un alias PLANO
 * —`#tramites-historial`, que existía y nadie usaba— pierde la sub-ruta por el
 * camino y abre la pestaña por defecto, así que el enlace «funciona» llevando al
 * sitio equivocado. Los dos alias planos se borraron el 2026-09-06; los que
 * quedan son sub-rutas de verdad.
 */

const DE_LA_RUTA: Record<string, string> = {
  nueva: 'catalogo', catalogo: 'catalogo',
  mine: 'mias', mis: 'mias', historial: 'mias',
  aprobaciones: 'aprobar', approve: 'aprobar',
  bandeja: 'bandeja', tipos: 'tipos',
};

export function Solicitudes() {
  const esGestion = Auth.isStaff();
  const porAprobar = usePorAprobar();
  const pendientes = porAprobar.data?.length ?? 0;
  // Gestión la ve siempre —es parte de su trabajo—; el resto solo si de verdad
  // tiene algo que decidir. Antes se mostraba a todo el mundo y competía
  // visualmente con las dos que sí usa cualquiera.
  const verAprobar = esGestion || pendientes > 0;

  const pestanas: Pestana[] = [
    { clave: 'catalogo', etiqueta: 'Catálogo', icono: 'bi-grid' },
    { clave: 'mias', etiqueta: 'Mis solicitudes', icono: 'bi-list-check' },
    ...(verAprobar
      ? [{ clave: 'aprobar', etiqueta: 'Por aprobar', icono: 'bi-check2-square', cuenta: pendientes }]
      : []),
    ...(esGestion
      ? [{ clave: 'bandeja', etiqueta: 'Bandeja', icono: 'bi-inbox' },
         { clave: 'tipos', etiqueta: 'Tipos', icono: 'bi-sliders' }]
      : []),
  ];

  const [tab, setTab] = useState(() => {
    const p = window._routeParams?.[0];
    const destino = p ? DE_LA_RUTA[p] : undefined;
    return destino ?? 'catalogo';
  });
  // Una pestaña que dejó de existir —«Por aprobar» cuando ya no queda nada que
  // firmar— no puede quedarse seleccionada: Radix pintaría un panel vacío sin
  // ninguna pestaña marcada.
  const activa = pestanas.some((p) => p.clave === tab) ? tab : 'catalogo';

  const [radicando, setRadicando] = useState<TipoDeSolicitud | null>(null);
  const [corrigiendo, setCorrigiendo] = useState<SolicitudDetalle | null>(null);
  const [avisoAprobador, setAvisoAprobador] = useState<{ radicado: string; texto: string } | null>(null);

  return (
    <div className="react-root flex flex-col gap-5 pb-10">
      <header>
        <h1 className="text-8 font-bold tracking-tight text-brand-ink">Solicitudes</h1>
        <p className="mt-1 text-3 text-muted">
          Tus trámites ante Bienestar: radícalos, sigue su estado y firma los que te toquen.
        </p>
      </header>

      <Tabs pestanas={pestanas} valor={activa} onCambio={setTab}>
        <TabPanel clave="catalogo">
          <Catalogo onElegir={setRadicando} />
        </TabPanel>
        <TabPanel clave="mias">
          <MisSolicitudes onCorregir={setCorrigiendo} />
        </TabPanel>
        {verAprobar && <TabPanel clave="aprobar"><PorAprobar /></TabPanel>}
        {esGestion && <TabPanel clave="bandeja"><Bandeja /></TabPanel>}
        {esGestion && <TabPanel clave="tipos"><Tipos /></TabPanel>}
      </Tabs>

      {/* Radicar */}
      <FormularioSolicitud
        abierto={radicando != null}
        onCerrar={() => setRadicando(null)}
        tipo={radicando}
        onEnviado={(r) => {
          Utils.toast(`Solicitud enviada · Radicado ${r.radicado}`, 'success');
          setTab('mias');
          // El servidor avisa si el trámite nace sin nadie que pueda aprobarlo.
          // No se mezcla con el toast de éxito: es otra cosa, dura más y hay que
          // leerla entera.
          if (r.aviso) setAvisoAprobador({ radicado: r.radicado, texto: r.aviso });
        }}
      />

      {/* Corregir una devuelta */}
      <FormularioSolicitud
        abierto={corrigiendo != null}
        onCerrar={() => setCorrigiendo(null)}
        tipo={corrigiendo
          ? { name: corrigiendo.type_name, icon: corrigiendo.type_icon,
              fields: corrigiendo.fields, flow: corrigiendo.flow }
          : null}
        solicitudId={corrigiendo?.id}
        valoresIniciales={corrigiendo?.form_data}
        avisoDevolucion={corrigiendo?.devolucion?.note ?? null}
        onEnviado={(r) => {
          if (r.aviso) Utils.toast(r.aviso, 'warning', 9000);
          else Utils.toast('Solicitud corregida y reenviada', 'success');
          setTab('mias');
        }}
      />

      <Dialog
        abierto={avisoAprobador != null}
        onCambio={(v) => { if (!v) setAvisoAprobador(null); }}
        titulo="Falta configurar tu aprobador"
        ancho="max-w-md"
        pie={<Button onClick={() => setAvisoAprobador(null)}>Entendido</Button>}
      >
        <p className="text-3 leading-relaxed text-ink">{avisoAprobador?.texto}</p>
        <p className="mt-2 text-3 leading-relaxed text-muted">
          Tu solicitud <strong className="text-ink-2">{avisoAprobador?.radicado}</strong> quedó
          guardada. No tienes que volver a radicarla.
        </p>
      </Dialog>
    </div>
  );
}

/* ── Catálogo ──────────────────────────────────────────────────────────── */

/** Los tipos que el colaborador puede radicar. Cada tarjeta es un `<button>` y
 *  no un `<div onclick>`: un div no recibe foco, no responde a Enter y el lector
 *  de pantalla no lo anuncia. */
function Catalogo({ onElegir }: { onElegir: (t: TipoDeSolicitud) => void }) {
  const consulta = useCatalogo();

  if (consulta.isError) {
    return <EstadoError err={consulta.error} que="el catálogo de solicitudes"
                        onReintentar={() => void consulta.refetch()} />;
  }
  if (consulta.isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-32 w-full rounded-md" />)}
      </div>
    );
  }
  const tipos = consulta.data ?? [];
  if (!tipos.length) {
    return <EstadoVacio icono="bi-file-earmark-text" titulo="No hay tipos de solicitud disponibles"
                        descripcion="Cuando Bienestar publique un trámite, aparecerá aquí." />;
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {tipos.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onElegir(t)}
          className={cn(
            'group flex flex-col items-start gap-2 rounded-md border border-line bg-surface p-4 text-left',
            'transition-colors hover:border-brand-ink hover:bg-sunken',
            'focus-visible:focus-anillo',
          )}
        >
          <i className={cn('bi', t.icon || 'bi-file-earmark-text', 'text-7 text-muted',
                           'transition-colors group-hover:text-brand-ink')} aria-hidden="true" />
          <span className="text-4 font-bold text-brand-ink">{t.name}</span>
          {t.description && (
            <span className="text-2 leading-relaxed text-muted">{t.description}</span>
          )}
          {t.category && (
            <span className="mt-auto rounded-full bg-sunken px-2 py-0.5 text-1 font-semibold uppercase tracking-wide text-muted">
              {t.category}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}
